// Availability is evaluated per segment inside an hourly card. If only part of
// the hour is unavailable, the free segment remains selectable; the entire card
// is disabled only when it contains no selectable time.
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { patientActions } from '../../core/actions';
import { useOrganization } from '../../configurations/OrganizationContext';
import {
  DOCTOR_LEAVE_EARLY_TIMINGS,
  APPT_MORNING_TIMINGS,
  APPT_EVENING_TIMINGS,
  Appointmant_schedule_session_booking_start_time,
  Appointmant_schedule_session_booking_end_time,
  Docter_leave_unavailable_slot_value,
  getOrgConstant,
} from '../../shared/constants';

const buildTimeSlots = (bookingStartTime, bookingEndTime) => [
  { start: bookingStartTime, end: '01:00' },
  { start: '01:00', end: '02:00' },
  { start: '02:00', end: '03:00' },
  { start: '03:00', end: '04:00' },
  { start: '04:00', end: '05:00' },
  { start: '05:00', end: '06:00' },
  { start: '06:00', end: '07:00' },
  { start: '07:00', end: '08:00' },
  { start: '08:00', end: '09:00' },
  { start: '09:00', end: '10:00' },
  { start: '10:00', end: '11:00' },
  { start: '11:00', end: '12:00' },
  { start: '12:00', end: '13:00' },
  { start: '13:00', end: '14:00' },
  { start: '14:00', end: '15:00' },
  { start: '15:00', end: '16:00' },
  { start: '16:00', end: '17:00' },
  { start: '17:00', end: '18:00' },
  { start: '18:00', end: '19:00' },
  { start: '19:00', end: '20:00' },
  { start: '20:00', end: '21:00' },
  { start: '21:00', end: '22:00' },
  { start: '22:00', end: '23:00' },
  { start: '23:00', end: bookingEndTime },
];


const toMinutes = (value) => {
  if (!value) return 0;
  const [hours, minutes] = String(value).split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return 0;
  return hours * 60 + minutes;
};

const addMinutes = (time, minutes) => {
  const total = toMinutes(time) + minutes;
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const to12HourFormat = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = String(time24).split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return String(time24);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
};

const normalizeTime = (value) => String(value || '').slice(0, 5);

// Converts keys like "nine_00_am", "8_00_am", or "09:00" to "09:00"
const parseTimeFromKey = (key) => {
  if (!key) return null;
  if (typeof key === 'string' && key.includes(':')) {
    return normalizeTime(key);
  }

  const wordToNum = {
    one: 1, two: 2, three: 3, four: 4, five: 5,
    six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
    eleven: 11, twelve: 12,
  };

  const match = String(key).match(/^([a-z0-9]+)_(\d{2})_(am|pm)$/i);
  if (!match) return null;

  let [, hourStr, mins, period] = match;
  let hour = wordToNum[hourStr.toLowerCase()] || parseInt(hourStr, 10);
  if (!hour || Number.isNaN(hour)) return null;

  if (period.toLowerCase() === 'pm' && hour < 12) hour += 12;
  if (period.toLowerCase() === 'am' && hour === 12) hour = 0;

  return `${String(hour).padStart(2, '0')}:${mins}`;
};

// Maps database leave fields to the 15-minute ranges shown in the availability grid.
const flattenLeaveSlotLookup = () => {
  const lookup = {};
  [
    ...DOCTOR_LEAVE_EARLY_TIMINGS,
    ...APPT_MORNING_TIMINGS,
    ...APPT_EVENING_TIMINGS,
  ].forEach((row) => {
    Object.entries(row).forEach(([key, value]) => {
      if (!key.startsWith('type') || !value) return;
      const timeKey = key.replace('type', 'time');
      const start = row[timeKey];
      if (start) {
        const [hour, minute] = String(start).split(':');
        const normalizedStart = `${String(Number(hour)).padStart(2, '0')}:${minute}`;
        lookup[value] = {
          start: normalizedStart,
          end: addMinutes(normalizedStart, 15),
        };
      }
    });
  });
  return lookup;
};

const LEAVE_SLOT_LOOKUP = flattenLeaveSlotLookup();

const resolveDoctorAndDate = (schedule = []) => {
  const firstRow = schedule.find(Boolean) || null;
  if (!firstRow) return { doctorId: '', date: '' };

  const doctorId =
    firstRow.doctor_id ??
    firstRow.doctorId ??
    firstRow.gvid ??
    firstRow.gvId ??
    '';

  const date =
    firstRow.session_date ??
    firstRow.blocked_date ??
    firstRow.date ??
    firstRow.apptdate ??
    '';

  return { doctorId, date };
};

const overlaps = (startA, endA, startB, endB) =>
  toMinutes(startA) < toMinutes(endB) && toMinutes(startB) < toMinutes(endA);

const classifySegment = (segmentStart, segmentEnd, bookings, leaves) => {
  const hasLeave = leaves.some((item) =>
    overlaps(segmentStart, segmentEnd, item.start, item.end)
  );
  if (hasLeave) return 'NOT_AVAILABLE';

  const hasBooking = bookings.some((item) =>
    overlaps(segmentStart, segmentEnd, item.start, item.end)
  );
  if (hasBooking) return 'BOOKED';

  return 'FREE';
};

const buildSegments = (slotStart, slotEnd, bookings, leaves) => {
  const boundaries = new Set([slotStart, slotEnd]);

  [...bookings, ...leaves].forEach((item) => {
    if (!overlaps(slotStart, slotEnd, item.start, item.end)) return;
    if (toMinutes(item.start) > toMinutes(slotStart) && toMinutes(item.start) < toMinutes(slotEnd)) {
      boundaries.add(item.start);
    }
    if (toMinutes(item.end) > toMinutes(slotStart) && toMinutes(item.end) < toMinutes(slotEnd)) {
      boundaries.add(item.end);
    }
  });

  const ordered = [...boundaries].sort((a, b) => toMinutes(a) - toMinutes(b));
  const segments = [];

  for (let i = 0; i < ordered.length - 1; i += 1) {
    const start = ordered[i];
    const end = ordered[i + 1];
    if (toMinutes(start) === toMinutes(end)) continue;
    const status = classifySegment(start, end, bookings, leaves);
    const prev = segments[segments.length - 1];
    if (prev && prev.status === status && prev.end === start) {
      prev.end = end;
    } else {
      segments.push({ status, start, end });
    }
  }

  if (segments.length === 0) {
    segments.push({ status: 'FREE', start: slotStart, end: slotEnd });
  }

  return segments;
};

const getSegmentLabel = (segment) => {
  if (segment.status === 'NOT_AVAILABLE') return 'NOT AVAILABLE';
  if (segment.status === 'BOOKED') return 'BUSY';
  return 'FREE';
};

const getSegmentColors = (segment, selected) => {
  if (segment.status === 'NOT_AVAILABLE') {
    return {
      background: '#e5e7eb',
      color: '#4b5563',
      borderColor: '#9ca3af',
    };
  }
  if (segment.status === 'BOOKED') {
    return {
      background: selected ? '#dbeafe' : '#fee2e2',
      color: '#b91c1c',
      borderColor: '#fecaca',
    };
  }
  return {
    background: selected ? '#dbeafe' : '#dcfce7',
    color: '#15803d',
    borderColor: '#bbf7d0',
  };
};

export default function AvailabilityViewer({
  title,
  schedule = [],
  loading = false,
  selectedTime,
  selectedEndTime,
  onSelect,
  currentBookingId,
  doctorId: doctorIdProp,
  date: dateProp,
}) {
  const dispatch = useDispatch();
  const org = useOrganization();
  const bookingStartTime = getOrgConstant(
    Appointmant_schedule_session_booking_start_time,
    org?.orgCode
  );
  const bookingEndTime = getOrgConstant(
    Appointmant_schedule_session_booking_end_time,
    org?.orgCode
  );
  const doctorLeaveUnavailableSlotValue = getOrgConstant(
    Docter_leave_unavailable_slot_value,
    org?.orgCode
  );
  const timeSlots = useMemo(
    () => buildTimeSlots(bookingStartTime, bookingEndTime),
    [bookingStartTime, bookingEndTime]
  );
  const [leaveSlots, setLeaveSlots] = useState([]);
  const [leaveLoading, setLeaveLoading] = useState(false);

  // useEffect(() => {
  //   let cancelled = false;
  //   const derived = resolveDoctorAndDate(schedule);
  //   const doctorId = doctorIdProp || derived.doctorId;
  //   const date = dateProp || derived.date;

  //   if (!doctorId || !date) {
  //     setLeaveSlots([]);
  //     setLeaveLoading(false);
  //     return undefined;
  //   }

  //   setLeaveLoading(true);
  //   dispatch(
  //     patientActions.getleaveCalender(doctorId, date, (resCb) => {
  //       if (cancelled) return;

  //       const leaveMap = resCb?.lc || resCb?.data?.lc || resCb?.data || {};
  //       const notAvailable = [];

  //       Object.entries(leaveMap || {}).forEach(([key, value]) => {
  //         if (value !== 'NA') return;

  //         // 1. Check mapped constant lookup
  //         const slotInfo = LEAVE_SLOT_LOOKUP[key];
  //         if (slotInfo) {
  //           notAvailable.push(slotInfo);
  //           return;
  //         }

  //         // 2. Parse direct/formatted keys (e.g. "nine_00_am", "09:00")
  //         const parsedStart = parseTimeFromKey(key);
  //         if (parsedStart) {
  //           notAvailable.push({
  //             start: parsedStart,
  //             end: addMinutes(parsedStart, 15),
  //           });
  //         }
  //       });

  //       setLeaveSlots(notAvailable);
  //       setLeaveLoading(false);
  //     })
  //   );

  //   return () => {
  //     cancelled = true;
  //   };
  // }, [schedule, dispatch, doctorIdProp, dateProp]);
useEffect(() => {
  let cancelled = false;
  const derived = resolveDoctorAndDate(schedule);
  const doctorId = doctorIdProp || derived.doctorId;
  const date = dateProp || derived.date;

  if (!doctorId || !date) {
    setLeaveSlots([]);
    setLeaveLoading(false);
    return undefined;
  }

  setLeaveLoading(true);
  dispatch(
    patientActions.getleaveCalender(doctorId, date, (resCb) => {
      if (cancelled) return;

      const leaveMap = resCb?.lc || resCb?.data?.lc || resCb?.data || {};
      const notAvailable = [];
      const supportedSlotKeys = Object.keys(LEAVE_SLOT_LOOKUP);
      const isFullDayLeave =
        supportedSlotKeys.length > 0 &&
        supportedSlotKeys.every(
          (key) => leaveMap[key] === doctorLeaveUnavailableSlotValue,
        );

      // A complete set of unavailable persisted slots represents Full-Day.
      if (isFullDayLeave) {
        setLeaveSlots([
          {
            start: bookingStartTime,
            end: bookingEndTime,
          },
        ]);
        setLeaveLoading(false);
        return;
      }

      Object.entries(leaveMap || {}).forEach(([key, value]) => {
        if (value !== doctorLeaveUnavailableSlotValue) return;

        // 1. Rely strictly on LEAVE_SLOT_LOOKUP from constants
        const slotInfo = LEAVE_SLOT_LOOKUP[key];
        if (slotInfo) {
          notAvailable.push(slotInfo);
          return;
        }

        // 2. Only parse keys that explicitly use colon time notation (e.g. "09:00")
        if (typeof key === 'string' && key.includes(':')) {
          const start = normalizeTime(key);
          const end = addMinutes(start, 15);
          notAvailable.push({ start, end });
        }
      });

      setLeaveSlots(notAvailable);
      setLeaveLoading(false);
    })
  );

  return () => {
    cancelled = true;
  };
}, [
  schedule,
  dispatch,
  doctorIdProp,
  dateProp,
  bookingStartTime,
  bookingEndTime,
  doctorLeaveUnavailableSlotValue,
]);
  const bookedRanges = useMemo(() => {
    return schedule
      .filter((item) => {
        const bookingId = item.booking_id ?? item.bookingId ?? item.id ?? null;
        if (currentBookingId && bookingId != null && String(bookingId) === String(currentBookingId)) {
          return false;
        }
        return Boolean(item.start_time && item.end_time);
      })
      .map((item) => ({
        start: normalizeTime(item.start_time),
        end: normalizeTime(item.end_time),
      }));
  }, [currentBookingId, schedule]);

  const isSelectedSlot = (slotStart, slotEnd) => {
    const selectedStart = selectedTime || '';
    const selectedEnd = selectedEndTime || '';
    return Boolean(selectedStart && selectedEnd && overlaps(slotStart, slotEnd, selectedStart, selectedEnd));
  };

  const hasLeaveConflict = (slotStart, slotEnd) =>
    leaveSlots.some((item) => overlaps(slotStart, slotEnd, item.start, item.end));

  const canSelectSegment = (segment) =>
    Boolean(onSelect) && segment.status == 'FREE';

  return (
    <div className="availability-container" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {title && (
        <h5 style={{ margin: 0, fontSize: '0.85rem', color: 'var(--gray-600)', fontWeight: 700 }}>
          {title}
        </h5>
      )}

      {loading || leaveLoading ? (
        <div className="flex-center" style={{ padding: '20px' }}>
          <div className="spinner spinner-sm" />
        </div>
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))',
              gap: '8px',
              background: 'white',
              padding: '12px',
              borderRadius: 8,
              border: '1px solid #dbe2ea',
              boxShadow: '0 1px 2px rgba(15,23,42,0.05)',
            }}
          >
            {timeSlots.map((slot) => {
              const segments = buildSegments(slot.start, slot.end, bookedRanges, leaveSlots);
              const slotHasLeave = hasLeaveConflict(slot.start, slot.end);
              const selected = isSelectedSlot(slot.start, slot.end);
              const selectableSegments = segments.filter(canSelectSegment);
              const preferredSelectableSegment = selectableSegments.reduce((preferred, segment) => {
                if (!preferred) return segment;
                const preferredDuration = toMinutes(preferred.end) - toMinutes(preferred.start);
                const segmentDuration = toMinutes(segment.end) - toMinutes(segment.start);
                return segmentDuration > preferredDuration ? segment : preferred;
              }, null);
              const slotCanBeSelected = Boolean(preferredSelectableSegment);

              return (
                <div
                  key={slot.start}
                  style={{
                    borderRadius: 6,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: selected ? '#2563eb' : slotHasLeave ? '#9ca3af' : '#cbd5e1',
                    background: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '78px',
                    transition: 'all 0.2s',
                    boxShadow: selected ? 'var(--shadow-md)' : 'none',
                    opacity: slotHasLeave ? 0.98 : 1,
                  }}
                >
                  <div
                    style={{
                      padding: '6px',
                      textAlign: 'center',
                      background: '#f8fafc',
                      fontSize: '0.68rem',
                      fontWeight: 800,
                      borderBottom: '1px solid #e2e8f0',
                      color: '#334155',
                    }}
                  >
                    {to12HourFormat(slot.start)} - {to12HourFormat(slot.end)}
                  </div>

                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      cursor: slotCanBeSelected ? 'pointer' : 'default',
                    }}
                  >
                    {segments.map((segment, idx) => {
                      const segmentSelected = isSelectedSlot(segment.start, segment.end);
                      const colors = getSegmentColors(segment, segmentSelected);
                      const isLast = idx === segments.length - 1;
                      const segmentCanBeSelected = canSelectSegment(segment);

                      return (
                        <div
                          key={`${segment.start}-${segment.end}-${idx}`}
                          onClick={() => {
                            if (!segmentCanBeSelected) return;
                            onSelect(segment.start, segment.end);
                          }}
                          style={{
                            flex: 1,
                            background: colors.background,
                            borderRight: isLast ? 'none' : '1px dashed #cbd5e1',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '4px',
                            textAlign: 'center',
                            transition: 'all 0.2s',
                            minWidth: 0,
                            color: colors.color,
                            cursor: segmentCanBeSelected ? 'pointer' : 'not-allowed',
                          }}
                        >
                          <span style={{ fontSize: '0.6rem', fontWeight: 800 }}>
                            {getSegmentLabel(segment)}
                          </span>
                          <span style={{ fontSize: '0.55rem', opacity: 0.8 }}>
                            {to12HourFormat(segment.start)}-{to12HourFormat(segment.end)}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {onSelect && (
                    <div
                      onClick={() => {
                        if (!preferredSelectableSegment) return;
                        onSelect(preferredSelectableSegment.start, preferredSelectableSegment.end);
                      }}
                      style={{
                        padding: '5px 4px',
                        textAlign: 'center',
                        background: selected ? '#2563eb' : slotHasLeave ? '#f8fafc' : '#f8fafc',
                        color: selected ? 'white' : slotHasLeave ? '#353434' : '#2563eb',
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        cursor: slotCanBeSelected ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {selected ? 'SELECTED' : slotCanBeSelected ? 'SELECT SLOT' : 'NOT AVAILABLE'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: '0.75rem', display: 'flex', gap: '1.5rem', fontSize: '0.75rem', fontWeight: 600, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '14px', height: '14px', background: '#f0fdf4', border: '1px solid #166534', borderRadius: '4px' }} />
              <span style={{ color: '#166534' }}>Available</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '14px', height: '14px', background: '#fee2e2', border: '1px solid #b91c1c', borderRadius: '4px' }} />
              <span style={{ color: '#b91c1c' }}>Scheduled</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '14px', height: '14px', background: '#d1d5db', border: '1px solid #4b5563', borderRadius: '4px' }} />
              <span style={{ color: '#4b5563' }}>Not Available</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
